import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Home as HomeIcon, Users, Copy, Check, Wifi, WifiOff, Loader2, UserRound, Share2 } from 'lucide-react';
import useScopaOnline from '../multiplayer/useScopaOnline';
import { ConnectionProblem } from '../components/ConnectionProblem';
import {
  MP_CONFIGURED,
  makeRoomCode,
  normalizeRoomCode,
  hasIdentity,
  loadNickname,
  saveNickname,
  ensurePlayerId,
  validateNickname,
  claimNickname,
} from '../multiplayer/mpConfig';

const NICK_ERRORS = {
  nickname_taken: 'Questo nickname è già in uso. Scegline un altro.',
  invalid_nickname: 'Nickname non valido. Usa 3-16 caratteri (lettere, numeri, spazio, - o _).',
  timeout: 'Server non raggiungibile. Riprova.',
  connection: 'Impossibile contattare il server. Riprova.',
  not_configured: 'Multiplayer non configurato.',
};

export default function OnlineLobby() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [roomCode, setRoomCode] = useState(null);
  const [joinInput, setJoinInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [inviteCopied, setInviteCopied] = useState(false);

  // STEP 3B — an invite link opens the lobby as /online?join=CODE. We capture it
  // once and auto-enter the join flow as soon as an identity exists (reusing the
  // existing nickname gate + join path; no manual code paste needed).
  const pendingJoinRef = useRef(
    (() => {
      const c = normalizeRoomCode(searchParams.get('join') || '');
      return c.length === 6 ? c : null;
    })()
  );

  // ---- Multiplayer identity gate (STEP 3A) ----
  const [identityReady, setIdentityReady] = useState(() => hasIdentity());
  const [nickInput, setNickInput] = useState(() => loadNickname() || '');
  const [nickError, setNickError] = useState('');
  const [nickBusy, setNickBusy] = useState(false);

  const submitNickname = useCallback(async () => {
    if (nickBusy) return;
    const v = validateNickname(nickInput);
    if (!v.ok) {
      setNickError(v.message);
      return;
    }
    setNickError('');
    setNickBusy(true);
    const res = await claimNickname(ensurePlayerId(), v.value);
    setNickBusy(false);
    if (res.ok) {
      saveNickname(res.nickname);
      setNickInput(res.nickname);
      setIdentityReady(true);
    } else {
      setNickError(NICK_ERRORS[res.error] || 'Errore imprevisto. Riprova.');
    }
  }, [nickBusy, nickInput]);

  // Room size chosen by the creator (sent to the server with the first hello).
  const [size, setSize] = useState(2);
  const [createSize, setCreateSize] = useState(null);
  const { connected, seat, view, error, offline, retry } = useScopaOnline(roomCode, createSize);

  const createRoom = useCallback(() => {
    setCreateSize(size);
    setRoomCode(makeRoomCode());
  }, [size]);
  const joinRoom = useCallback(() => {
    const code = normalizeRoomCode(joinInput);
    if (code.length === 6) setRoomCode(code);
  }, [joinInput]);

  const copyCode = useCallback(() => {
    if (!roomCode) return;
    try {
      navigator.clipboard.writeText(roomCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard unavailable */
    }
  }, [roomCode]);

  // Auto-enter the join flow once we have an identity (works whether the
  // nickname already existed or was just chosen via the gate).
  useEffect(() => {
    if (identityReady && pendingJoinRef.current && !roomCode) {
      setRoomCode(pendingJoinRef.current);
      pendingJoinRef.current = null;
    }
  }, [identityReady, roomCode]);

  const inviteUrl = roomCode ? `${window.location.origin}/online?join=${roomCode}` : '';

  const shareInvite = useCallback(async () => {
    if (!inviteUrl) return;
    const shareData = {
      title: 'Scopa Napoletana',
      text: 'Ti sfido a Scopa! Entra nella mia partita:',
      url: inviteUrl,
    };
    if (typeof navigator !== 'undefined' && navigator.share) {
      try {
        await navigator.share(shareData);
      } catch {
        /* user cancelled or share failed — no fallback needed here */
      }
      return;
    }
    // Fallback: copy the link to the clipboard.
    try {
      await navigator.clipboard.writeText(inviteUrl);
      setInviteCopied(true);
      setTimeout(() => setInviteCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  }, [inviteUrl]);

  // Authoritative room size and occupancy come from the server view.
  const roomSize = (view && view.numPlayers) || 2;
  const seatedCount = view && view.seatsFilled ? Object.values(view.seatsFilled).filter(Boolean).length : 0;
  const multi = roomSize > 2;
  const bothSeated = multi
    ? seatedCount === roomSize
    : view && view.seatsFilled && view.seatsFilled.A && view.seatsFilled.B;
  const roomFull = error === 'room_full';
  const myNickname = loadNickname();
  const opponentNickname = (view && view.names && view.names.opponent) || null;

  const statusLabel = useMemo(() => {
    if (roomFull) return 'Stanza piena';
    if (!connected) return 'Connessione in corso…';
    if (multi) {
      if (bothSeated) return `Tutti i giocatori connessi (${seatedCount} su ${roomSize})`;
      return `In attesa dei giocatori: ${seatedCount} su ${roomSize}`;
    }
    if (bothSeated) return 'Avversario connesso';
    return 'In attesa dell’avversario…';
  }, [roomFull, connected, bothSeated, multi, seatedCount, roomSize]);

  return (
    <div
      className="relative min-h-[100dvh] w-full overflow-hidden felt-bg flex justify-center"
      data-testid="online-lobby"
    >
      <div className="w-full max-w-md flex flex-col h-full px-5 pt-4 pb-6">
        {/* Top bar */}
        <div className="flex items-center justify-between mb-6">
          <button
            type="button"
            onClick={() => navigate('/')}
            data-testid="online-home-btn"
            aria-label="Torna alla schermata iniziale"
            className="w-9 h-9 rounded-full bg-[#2C1A14] border border-[#D4AF37]/30 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
          >
            <HomeIcon className="w-4 h-4" />
          </button>
          <div className="flex items-center gap-2 text-[#D4AF37]">
            <Users className="w-5 h-5" aria-hidden="true" />
            <span className="font-serif text-lg text-[#FDFBF7]">Gioca online</span>
          </div>
          <span className="w-9" aria-hidden="true" />
        </div>

        {!MP_CONFIGURED ? (
          <div
            className="mt-8 rounded-2xl bg-black/30 border border-[#D4AF37]/20 p-5 text-center"
            data-testid="online-not-configured"
            role="status"
          >
            <p className="font-sans text-sm text-[#E0E0D1]">
              Il multiplayer non è ancora configurato. Manca l’indirizzo del server
              (<span className="text-[#D4AF37]">REACT_APP_MP_URL</span>).
            </p>
          </div>
        ) : !identityReady ? (
          /* ---- Nickname identity gate (STEP 3A) ---- */
          <motion.div
            className="flex flex-col gap-5 mt-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="nickname-gate"
          >
            <div className="flex items-center gap-2 text-[#D4AF37]">
              <UserRound className="w-5 h-5" aria-hidden="true" />
              <span className="font-serif text-lg text-[#FDFBF7]">Scegli il tuo nickname</span>
            </div>
            <p className="font-sans text-sm text-[#E0E0D1]/80">
              Ti servirà solo per giocare online. La modalità contro il computer non lo richiede.
            </p>
            <div className="flex flex-col gap-2">
              <label htmlFor="nickname" className="sr-only">
                Nickname
              </label>
              <input
                id="nickname"
                data-testid="nickname-input"
                value={nickInput}
                onChange={(e) => {
                  setNickInput(e.target.value);
                  if (nickError) setNickError('');
                }}
                onKeyDown={(e) => e.key === 'Enter' && submitNickname()}
                placeholder="Il tuo nickname"
                inputMode="text"
                autoComplete="off"
                maxLength={16}
                disabled={nickBusy}
                aria-invalid={!!nickError}
                aria-describedby={nickError ? 'nickname-error' : undefined}
                className="w-full rounded-xl bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans text-base px-4 py-3 placeholder:text-[#E0E0D1]/30 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/40 disabled:opacity-50"
              />
              {nickError && (
                <p id="nickname-error" role="alert" data-testid="nickname-error" className="font-sans text-sm text-red-400">
                  {nickError}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={submitNickname}
              disabled={nickBusy}
              data-testid="nickname-submit-btn"
              className="w-full flex items-center justify-center gap-2 rounded-2xl bg-[#D4AF37] text-[#14452F] font-sans font-bold text-base py-4 shadow-lg transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 active:translate-y-px disabled:opacity-60"
            >
              {nickBusy && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {nickBusy ? 'Verifica…' : 'Continua'}
            </button>
          </motion.div>
        ) : !roomCode ? (
          /* ---- Choice: create or join ---- */
          <motion.div
            className="flex flex-col gap-6 mt-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
          >
            <div className="flex flex-col gap-2" role="radiogroup" aria-labelledby="room-size-label" data-testid="room-size-picker">
              <span id="room-size-label" className="font-sans text-sm text-[#E0E0D1]">
                Numero di giocatori
              </span>
              <div className="grid grid-cols-3 gap-2">
                {[2, 3, 4].map((n) => (
                  <button
                    key={n}
                    type="button"
                    role="radio"
                    aria-checked={size === n}
                    aria-label={n === 4 ? '4 giocatori, due squadre' : `${n} giocatori`}
                    onClick={() => setSize(n)}
                    data-testid={`room-size-${n}`}
                    className={`rounded-xl border py-2.5 font-sans font-semibold transition-colors focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50 ${
                      size === n
                        ? 'bg-[#D4AF37] text-[#14452F] border-[#D4AF37]'
                        : 'bg-[#2C1A14] text-[#FDFBF7] border-[#D4AF37]/40 hover:bg-[#3D261E]'
                    }`}
                  >
                    {n}
                  </button>
                ))}
              </div>
              <p className="font-sans text-xs text-[#E0E0D1]/60" data-testid="room-size-hint">
                {size === 2 ? 'Uno contro uno.' : size === 3 ? 'Tutti contro tutti, punteggio personale.' : 'Due squadre: giocatori 1 e 3 contro 2 e 4.'}
              </p>
            </div>

            <button
              type="button"
              onClick={createRoom}
              data-testid="create-room-btn"
              className="w-full rounded-2xl bg-[#D4AF37] text-[#14452F] font-sans font-bold text-base py-4 shadow-lg transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 active:translate-y-px"
            >
              Crea una partita
            </button>

            <div className="flex items-center gap-3 text-[#E0E0D1]/50 text-xs uppercase tracking-widest">
              <span className="flex-1 h-px bg-white/10" />
              oppure
              <span className="flex-1 h-px bg-white/10" />
            </div>

            <div className="flex flex-col gap-3">
              <label htmlFor="join-code" className="font-sans text-sm text-[#E0E0D1]">
                Entra con un codice
              </label>
              <div className="flex gap-2">
                <input
                  id="join-code"
                  data-testid="join-code-input"
                  value={joinInput}
                  onChange={(e) => setJoinInput(normalizeRoomCode(e.target.value))}
                  onKeyDown={(e) => e.key === 'Enter' && joinRoom()}
                  placeholder="ABC123"
                  inputMode="text"
                  autoCapitalize="characters"
                  autoComplete="off"
                  maxLength={6}
                  className="flex-1 rounded-xl bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-mono text-lg tracking-[0.3em] text-center px-3 py-3 placeholder:text-[#E0E0D1]/30 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/40"
                />
                <button
                  type="button"
                  onClick={joinRoom}
                  disabled={normalizeRoomCode(joinInput).length !== 6}
                  data-testid="join-room-btn"
                  className="rounded-xl bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold px-5 transition-colors hover:bg-[#3D261E] disabled:opacity-40 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
                >
                  Entra
                </button>
              </div>
            </div>
          </motion.div>
        ) : (
          /* ---- Waiting room ---- */
          <motion.div
            className="flex flex-col items-center gap-6 mt-4"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="online-waiting-room"
          >
            <div className="w-full rounded-2xl bg-black/25 border border-[#D4AF37]/25 p-5 text-center">
              <p className="font-sans text-xs uppercase tracking-widest text-[#E0E0D1]/60 mb-2">
                Codice partita
              </p>
              <div className="flex items-center justify-center gap-3">
                <span
                  className="font-mono text-4xl font-bold tracking-[0.35em] text-[#D4AF37]"
                  data-testid="room-code-value"
                >
                  {roomCode}
                </span>
                <button
                  type="button"
                  onClick={copyCode}
                  data-testid="copy-code-btn"
                  aria-label="Copia il codice partita"
                  className="w-9 h-9 rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 flex items-center justify-center text-[#FDFBF7] transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
                >
                  {copied ? <Check className="w-4 h-4 text-[#D4AF37]" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
              <p className="mt-3 font-sans text-sm text-[#E0E0D1]/80">
                {multi
                  ? `Condividi questo codice con gli altri giocatori. Partita da ${roomSize}${roomSize === 4 ? ', a squadre' : ''}.`
                  : 'Condividi questo codice con l’altro giocatore per iniziare.'}
              </p>
              {myNickname && (
                <p className="mt-3 font-sans text-sm text-[#FDFBF7]" data-testid="online-my-nickname">
                  Il tuo nickname:{' '}
                  <span className="text-[#D4AF37] font-semibold">{myNickname}</span>
                </p>
              )}
            </div>

            {/* Invite link (STEP 3B) — native share sheet, clipboard fallback */}
            <button
              type="button"
              onClick={shareInvite}
              data-testid="share-invite-btn"
              aria-label="Condividi invito tramite link"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold py-3 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50 active:translate-y-px"
            >
              {inviteCopied ? (
                <Check className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              ) : (
                <Share2 className="w-4 h-4 text-[#D4AF37]" aria-hidden="true" />
              )}
              {inviteCopied ? 'Link copiato' : 'Condividi invito'}
            </button>
            <p className="sr-only" role="status" aria-live="polite">
              {inviteCopied ? 'Link di invito copiato negli appunti.' : ''}
            </p>

            {/* Status */}
            {offline && !connected && !roomFull ? (
              <ConnectionProblem onRetry={retry} />
            ) : (
            <div
              className="flex items-center gap-2 rounded-full bg-black/30 px-4 py-2"
              role="status"
              aria-live="polite"
              data-testid="online-status"
            >
              {roomFull ? (
                <WifiOff className="w-4 h-4 text-red-400" aria-hidden="true" />
              ) : !connected ? (
                <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" aria-hidden="true" />
              ) : bothSeated ? (
                <Wifi className="w-4 h-4 text-emerald-400" aria-hidden="true" />
              ) : (
                <Loader2 className="w-4 h-4 text-[#D4AF37] animate-spin" aria-hidden="true" />
              )}
              <span className="font-sans text-sm text-[#FDFBF7]">{statusLabel}</span>
            </div>
            )}

            {seat && !roomFull && (
              <p className="font-sans text-xs text-[#E0E0D1]/60" data-testid="online-seat">
                Sei il giocatore {'ABCD'.indexOf(seat) + 1}.
                {multi && roomSize === 4 && ` Squadra: giocatori ${seat === 'A' || seat === 'C' ? '1 e 3' : '2 e 4'}.`}
              </p>
            )}

            {multi && !roomFull && view && view.players && (
              <ul className="w-full flex flex-col gap-1.5" aria-label="Partecipanti" data-testid="online-participants">
                {view.players.map((p) => (
                  <li key={p.seat} className="flex items-center justify-between rounded-xl bg-black/25 border border-white/10 px-3 py-2 font-sans text-sm" data-testid={`participant-${p.seat}`}>
                    <span className="text-[#FDFBF7]">
                      {'ABCD'.indexOf(p.seat) + 1}. {p.filled ? p.nickname || 'Giocatore' : 'Posto libero'}
                      {p.seat === seat ? ' (tu)' : ''}
                    </span>
                    <span className="text-xs text-[#E0E0D1]/70">
                      {roomSize === 4 ? `Squadra ${p.side === 'AC' ? '1+3' : '2+4'} · ` : ''}
                      {p.filled ? (p.online ? 'collegato' : 'non collegato') : 'in attesa'}
                    </span>
                  </li>
                ))}
              </ul>
            )}

            {roomFull ? (
              <button
                type="button"
                onClick={() => {
                  setRoomCode(null);
                  setJoinInput('');
                }}
                data-testid="online-back-btn"
                className="w-full rounded-full bg-[#2C1A14] border border-[#D4AF37]/40 text-[#FDFBF7] font-sans font-semibold py-3 transition-colors hover:bg-[#3D261E] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#D4AF37]/50"
              >
                Indietro
              </button>
            ) : bothSeated ? (
              <div
                className="w-full flex flex-col items-center gap-3 rounded-2xl bg-[#14452F]/60 border border-[#D4AF37]/30 p-4 text-center"
                data-testid="online-ready"
              >
                <p className="font-sans text-sm text-[#FDFBF7]" data-testid="online-opponent-nickname">
                  {multi ? (
                    'Tutti i giocatori sono connessi.'
                  ) : opponentNickname ? (
                    <>
                      Avversario:{' '}
                      <span className="text-[#D4AF37] font-semibold">{opponentNickname}</span> connesso.
                    </>
                  ) : (
                    'Avversario connesso.'
                  )}{' '}
                  Potete iniziare la partita.
                </p>
                <button
                  type="button"
                  onClick={() => navigate(`/online/game?room=${roomCode}`)}
                  data-testid="online-start-btn"
                  className="w-full rounded-full bg-[#D4AF37] text-[#14452F] font-sans font-bold py-3 transition-colors hover:bg-[#F2D675] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#F2D675]/60 active:translate-y-px"
                >
                  Inizia a giocare
                </button>
              </div>
            ) : null}
          </motion.div>
        )}
      </div>
    </div>
  );
}
